"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2, ShieldCheck } from "lucide-react";

/**
 * Footer newsletter capture (WC2). Posts to /api/newsletter/subscribe and,
 * on success, reveals the first-order discount code. Themed for the dark
 * footer (header-* tokens). Self-contained client island inside the RSC footer.
 */
export function NewsletterSignup() {
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<"new" | "already" | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- keep the SSR form inert until React owns its controlled input and submit handler.
    setMounted(true);
  }, []);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "footer" }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        error?: string;
        outcome?: string;
      };
      if (!res.ok) {
        toast.error(json.error ?? "Couldn't subscribe right now.");
        return;
      }
      setDone(json.outcome === "already" ? "already" : "new");
      toast.success("You're on the list!");
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done !== null) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-header-border bg-header-hover px-4 py-3" role="status" aria-live="polite">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-header-fg" aria-hidden="true" />
        <p className="text-sm text-header-muted">
          {done === "already" ? (
            <>You&apos;re already on our list — thank you!</>
          ) : (
            <>You&apos;re on the list — thank you! Look out for new collections &amp; seasonal flowers.</>
          )}
        </p>
      </div>
    );
  }

  return (
    <div>
      <form onSubmit={submit} className="flex flex-col gap-2.5 sm:flex-row" aria-label="Newsletter signup">
        <label htmlFor="newsletter-email" className="sr-only">Email address</label>
        <div className="relative flex-1">
          <Mail
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-header-muted/70"
            aria-hidden="true"
          />
          <input
            id="newsletter-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            disabled={!mounted || submitting}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            maxLength={254}
            className="min-h-11.5 w-full rounded-lg border border-header-border bg-header-bg pl-10 pr-3.5 text-sm text-header-fg placeholder:text-header-muted/60 outline-hidden transition-colors focus-visible:border-header-accent focus-visible:ring-2 focus-visible:ring-header-accent"
          />
        </div>
        <button
          type="submit"
          disabled={!mounted || submitting}
          className="inline-flex min-h-11.5 w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-header-fg px-6 text-sm font-semibold text-header-bg transition-opacity hover:opacity-90 disabled:opacity-60 sm:w-auto"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Joining…
            </>
          ) : (
            "Subscribe"
          )}
        </button>
      </form>
      <p className="mt-2.5 flex items-center gap-1.5 text-xs text-header-muted/70">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        No spam — unsubscribe anytime.
      </p>
    </div>
  );
}
